/**
 * @module BriefingBoardView
 * @purpose L0 Briefing Board — virtualised top-N cluster tiles led by AI headlines with deterministic fallback.
 *   Adds provider selector, per-cluster compare, and board-wide generation with ETA confirm (FR7/FR8).
 * @depends useBriefingBoard, useBoardWideGenerate, useReasoningRun, BriefingTile,
 *   BoardWideGenerateDialog, ReasoningLoadingState, ReasoningEmptyState, ReasoningErrorState, react-window
 * @dependedOnBy CaseAnalysisTab (via AnalysisSubTab 'briefingBoard' case)
 *
 * @rules
 * - Performance: react-window List with useDynamicRowHeight keeps ≤100 tiles in DOM; first paint
 *     must NOT block on AI warm — tiles render immediately with deterministic fallback
 * - State: local expandedSet (Set<string>); board data via useBriefingBoard
 * - Errors: loading/empty/error states delegated to Reasoning*State components
 */

import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { List, useDynamicRowHeight } from 'react-window'
import { Download, Zap } from 'lucide-react'
import { useReasoningRun } from '../../../../../hooks/use-reasoning'
import { useBriefingBoard } from '../../../../../hooks/use-cluster-briefing'
import { useBoardWideGenerate } from '../../../../../hooks/use-board-wide-generate'
import { ReasoningLoadingState } from '../ReasoningLoadingState'
import { ReasoningEmptyState } from '../ReasoningEmptyState'
import { ReasoningErrorState } from '../ReasoningErrorState'
import { BriefingTile } from './BriefingTile'
import { BoardWideGenerateDialog } from './BoardWideGenerateDialog'
import { exportBriefingBoardPdf } from './briefing-pdf-export'
import type { ClusterBriefing, ClusterSummary } from '../../../../../types/reasoning'

const DEFAULT_LIST_HEIGHT_PX = 600
const TOP_N = 20

interface BriefingBoardViewProps {
  caseId: string
}

interface BoardHeaderProps {
  clusterCount: number
  total: number
  warming: boolean
  onExportPdf: () => void
  onBoardWideGenerate: () => void
}

function BoardHeader({
  clusterCount,
  total,
  warming,
  onExportPdf,
  onBoardWideGenerate,
}: BoardHeaderProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const MONO = { fontFamily: 'var(--font-mono)', fontSize: '0.7rem' }
  const BTN: React.CSSProperties = {
    ...MONO,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    borderRadius: '4px',
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--accent)',
    padding: '3px 10px',
    cursor: 'pointer',
  }
  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-3 border-b px-4 py-3"
      style={{ borderColor: 'var(--border)' }}
    >
      <span style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', fontWeight: 600 }}>
        {t('reasoning.briefingBoard.title')}
      </span>
      {/*
        The provider selector lived here. Which LLM handles case evidence is a platform decision,
        never a tenant one (product-owner decision 2026-07-30), so it is not offered at case level.
        The comparison view below is unaffected — briefings from both providers are still displayed
        side by side when both exist; only the choosing has moved to platform settings.
      */}
      <div className="ml-auto flex items-center gap-2">
        {warming && (
          <span style={{ ...MONO, color: 'var(--text-dim)' }}>
            {t('reasoning.briefingBoard.header.warming')}
          </span>
        )}
        {clusterCount > 0 && (
          <span style={{ ...MONO, color: 'var(--text-dim)' }}>
            {t('reasoning.briefingBoard.header.topN', { shown: clusterCount, total })}
          </span>
        )}
        {clusterCount > 0 && (
          <button type="button" style={BTN} onClick={onBoardWideGenerate}>
            <Zap className="h-3.5 w-3.5" />
            {t('reasoning.briefingBoard.header.boardWideRegenerate')}
          </button>
        )}
        {clusterCount > 0 && (
          <button type="button" style={BTN} onClick={onExportPdf}>
            <Download className="h-3.5 w-3.5" />
            {t('reasoning.briefingBoard.header.exportPdf')}
          </button>
        )}
      </div>
    </div>
  )
}

interface TileRowSharedProps {
  caseId: string
  runId: string
  clusters: ClusterSummary[]
  headlines: Map<string, string>
  summaries: Map<string, string>
  aiUnavailable: boolean
  expandedSet: Set<string>
  onToggle: (id: string) => void
}

function TileRow({
  ariaAttributes,
  index,
  style,
  caseId,
  runId,
  clusters,
  headlines,
  summaries,
  aiUnavailable,
  expandedSet,
  onToggle,
}: {
  ariaAttributes: Record<string, unknown>
  index: number
  style: React.CSSProperties
} & TileRowSharedProps): React.ReactElement | null {
  const cluster = clusters[index]
  if (cluster === undefined) return null
  return (
    <div style={style} {...ariaAttributes}>
      <BriefingTile
        cluster={cluster}
        headline={headlines.get(cluster.clusterId)}
        summary={summaries.get(cluster.clusterId)}
        aiUnavailable={aiUnavailable}
        expanded={expandedSet.has(cluster.clusterId)}
        caseId={caseId}
        runId={runId}
        onToggle={() => { onToggle(cluster.clusterId) }}
      />
    </div>
  )
}

interface BoardListProps {
  caseId: string
  runId: string
  clusters: ClusterSummary[]
  total: number
  headlines: Map<string, string>
  summaries: Map<string, string>
  briefings: Map<string, ClusterBriefing>
  aiUnavailable: boolean
  warming: boolean
  onBoardWideGenerate: () => void
}

function BoardList({
  caseId,
  runId,
  clusters,
  total,
  headlines,
  summaries,
  briefings,
  aiUnavailable,
  warming,
  onBoardWideGenerate,
}: BoardListProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set<string>())
  const dynamicRowHeight = useDynamicRowHeight({ defaultRowHeight: 72 })
  const generatedAt = useMemo(() => new Date().toLocaleString(), [])

  const handleToggle = useCallback((id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
  }, [])

  const handleExportPdf = useCallback(() => {
    exportBriefingBoardPdf(
      { clusters, briefings, t, caseId, runId, total, generatedAt },
      `briefing-board-${runId}.pdf`,
    )
  }, [clusters, briefings, t, caseId, runId, total, generatedAt])

  const rowProps: TileRowSharedProps = {
    caseId, runId, clusters, headlines, summaries,
    aiUnavailable, expandedSet, onToggle: handleToggle,
  }

  return (
    <div>
      <BoardHeader
        clusterCount={clusters.length}
        total={total}
        warming={warming}
        onExportPdf={handleExportPdf}
        onBoardWideGenerate={onBoardWideGenerate}
      />
      <List<TileRowSharedProps>
        rowCount={clusters.length}
        rowHeight={dynamicRowHeight}
        rowComponent={TileRow}
        rowProps={rowProps}
        style={{ height: `${String(DEFAULT_LIST_HEIGHT_PX)}px` }}
      />
    </div>
  )
}

type BoardBodyState = 'loading' | 'error' | 'empty' | 'list'

function resolveBoardBodyState(
  loading: boolean,
  error: string | null,
  clusterCount: number,
): BoardBodyState {
  if (loading) return 'loading'
  if (error !== null) return 'error'
  if (clusterCount === 0) return 'empty'
  return 'list'
}

/**
 * L0 Briefing Board view — renders the top-N clusters as BriefingTiles with AI or
 * deterministic fallback headlines. Includes provider selector and board-wide generation (FR7/FR8).
 *
 * @behaviour First paint shows clusters immediately with deterministic fallbacks;
 *   AI headlines slot in when the batch warm resolves. Provider selector controls which
 *   profile is used for per-cluster and board-wide generation. Board-wide generation shows
 *   an ETA confirm dialog before launching (FR7 — no silent multi-hour churn).
 * @errors Loading/empty/error states via the shared Reasoning*State components.
 */
export function BriefingBoardView({ caseId }: BriefingBoardViewProps): React.ReactElement {
  const { run, loading: runLoading, error: runError, refetch: refetchRun } = useReasoningRun(caseId, true)
  const runId = run?.reasoningRunId ?? null
  const [showBoardWideDialog, setShowBoardWideDialog] = useState(false)

  const {
    clusters, total, headlines, summaries, briefings,
    aiUnavailable, loading: boardLoading, warming, error: boardError, refetch: refetchBoard,
  } = useBriefingBoard(caseId, runId, TOP_N)

  const {
    jobs, estimateSeconds, generating, error: generateError,
    providerNotConfigured, start, cancel, reset,
  } = useBoardWideGenerate()

  const loading = runLoading || boardLoading
  const error = runError ?? boardError

  const handleRetry = useCallback(() => {
    void refetchRun()
    void refetchBoard()
  }, [refetchRun, refetchBoard])

  const handleBoardWideGenerate = useCallback(() => {
    reset()
    setShowBoardWideDialog(true)
  }, [reset])

  const handleDialogClose = useCallback(() => {
    setShowBoardWideDialog(false)
    reset()
  }, [reset])

  const bodyState = resolveBoardBodyState(loading, error, clusters.length)

  if (bodyState === 'loading') return <ReasoningLoadingState />
  if (bodyState === 'error') return <ReasoningErrorState onRetry={handleRetry} />
  if (bodyState === 'empty' || runId === null) return <ReasoningEmptyState />

  return (
    <BoardBody
      caseId={caseId}
      runId={runId}
      clusters={clusters}
      total={total}
      headlines={headlines}
      summaries={summaries}
      briefings={briefings}
      aiUnavailable={aiUnavailable}
      warming={warming}
      handleBoardWideGenerate={handleBoardWideGenerate}
      showBoardWideDialog={showBoardWideDialog}
      jobs={jobs}
      estimateSeconds={estimateSeconds}
      generating={generating}
      generateError={generateError}
      providerNotConfigured={providerNotConfigured}
      start={start}
      cancel={cancel}
      handleDialogClose={handleDialogClose}
    />
  )
}

interface BoardBodyProps {
  caseId: string
  runId: string
  clusters: ClusterSummary[]
  total: number
  headlines: Map<string, string>
  summaries: Map<string, string>
  briefings: Map<string, ClusterBriefing>
  aiUnavailable: boolean
  warming: boolean
  handleBoardWideGenerate: () => void
  showBoardWideDialog: boolean
  jobs: import('../../../../../hooks/use-board-wide-generate').BoardJobProgress[]
  estimateSeconds: number
  generating: boolean
  generateError: string | null
  providerNotConfigured: boolean
  start: (params: import('../../../../../hooks/use-board-wide-generate').BoardWideStartParams) => Promise<void>
  cancel: () => void
  handleDialogClose: () => void
}

function BoardBody({
  caseId, runId, clusters, total, headlines, summaries, briefings,
  aiUnavailable, warming, handleBoardWideGenerate,
  showBoardWideDialog, jobs, estimateSeconds, generating, generateError,
  providerNotConfigured, start, cancel, handleDialogClose,
}: BoardBodyProps): React.ReactElement {
  return (
    <div className="flex flex-col" style={{ background: 'var(--bg)' }}>
      <BoardList
        caseId={caseId}
        runId={runId}
        clusters={clusters}
        total={total}
        headlines={headlines}
        summaries={summaries}
        briefings={briefings}
        aiUnavailable={aiUnavailable}
        warming={warming}
        onBoardWideGenerate={handleBoardWideGenerate}
      />
      {showBoardWideDialog && (
        <BoardWideGenerateDialog
          caseId={caseId}
          runId={runId}
          jobs={jobs}
          estimateSeconds={estimateSeconds}
          generating={generating}
          error={generateError}
          providerNotConfigured={providerNotConfigured}
          onStart={start}
          onCancel={cancel}
          onClose={handleDialogClose}
        />
      )}
    </div>
  )
}
